/**
 * POSReceiptCart — pane з кошиком чека (60% desktop / 100% mobile).
 *
 * Items list, customer card lookup, підсумки, налаштування знижки/бонусів.
 */
import { useState } from 'react';
import type { ReceiptCart } from '@/hooks/pos/useReceiptCart';
import { retailCustomerCards, type CustomerCardLookup } from '@/api/retailCustomerCards';
import type { LoyaltySettings } from '@/api/retailLoyaltySettings';

interface Props {
  cart: ReceiptCart;
  organizationId: string;
  customerCard: CustomerCardLookup | null;
  setCustomerCard: (c: CustomerCardLookup | null) => void;
  loyaltySettings: LoyaltySettings | null;
}

export default function POSReceiptCart({
  cart,
  organizationId,
  customerCard,
  setCustomerCard,
  loyaltySettings,
}: Props) {
  const [cardCode, setCardCode] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);

  const lookupCard = async () => {
    setLookupError(null);
    if (!cardCode) {
      setLookupError('Введіть код картки');
      return;
    }
    try {
      const c = await retailCustomerCards.lookup({
        organization_id: organizationId,
        card_code: cardCode,
      });
      setCustomerCard(c);
      setCardCode('');
    } catch (e) {
      setLookupError(
        e instanceof Error && 'response' in e
          ? 'Картку не знайдено'
          : 'Помилка пошуку',
      );
    }
  };

  const fmt = (v: number) =>
    v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const accrualPercent = loyaltySettings ? Number(loyaltySettings.accrual_percent) : 0;
  const willAccrue = customerCard && accrualPercent > 0
    ? (cart.summary.total * accrualPercent) / 100
    : 0;

  return (
    <div className="pos-receipt-cart">
      <div className="pos-cart__header">
        <span>🧾 Чек</span>
        <span>{cart.summary.itemsCount} позицій</span>
      </div>

      <div className="pos-card-lookup">
        <input
          type="text"
          placeholder="Код картки клієнта"
          value={cardCode}
          onChange={(e) => setCardCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') lookupCard();
          }}
        />
        <button onClick={lookupCard}>Знайти</button>
        {customerCard && (
          <button onClick={() => setCustomerCard(null)} title="Очистити">
            ✕
          </button>
        )}
      </div>
      {lookupError && (
        <div className="pos-card-info" style={{ color: 'var(--danger)' }}>
          {lookupError}
        </div>
      )}
      {customerCard && (
        <div className="pos-card-info">
          ✓ {customerCard.holder_name ?? '(без імені)'} · Бонусний баланс:{' '}
          <strong>{fmt(Number(customerCard.bonus_balance))}</strong>
        </div>
      )}

      <div className="pos-cart__list">
        {cart.isEmpty ? (
          <div className="pos-cart__empty">Кошик порожній</div>
        ) : (
          cart.items.map((it) => (
            <div key={it.key} className="pos-cart__item">
              <div>
                <div className="pos-cart__item-name">{it.name}</div>
                <div className="pos-cart__item-meta">
                  {fmt(it.price_per_unit)} грн × {it.quantity} ={' '}
                  <strong>{fmt(it.price_per_unit * it.quantity)} грн</strong>
                </div>
              </div>
              <div className="pos-cart__qty">
                <button
                  onClick={() => cart.updateQuantity(it.key, it.quantity - 1)}
                  aria-label="Зменшити"
                >
                  −
                </button>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={it.quantity}
                  onChange={(e) =>
                    cart.updateQuantity(it.key, Number(e.target.value) || 0)
                  }
                />
                <button
                  onClick={() => cart.updateQuantity(it.key, it.quantity + 1)}
                  aria-label="Збільшити"
                >
                  +
                </button>
              </div>
              <button
                className="pos-cart__remove"
                onClick={() => cart.removeItem(it.key)}
                aria-label="Видалити"
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>

      <div className="pos-cart__totals">
        <div className="pos-cart__totals-row">
          <span>Підсумок:</span>
          <span>{fmt(cart.summary.subtotal)} грн</span>
        </div>
        {cart.discountAmount > 0 && (
          <div className="pos-cart__totals-row">
            <span>Знижка:</span>
            <span>−{fmt(cart.discountAmount)} грн</span>
          </div>
        )}
        {cart.bonusWriteoff > 0 && (
          <div className="pos-cart__totals-row">
            <span>Списано бонусів:</span>
            <span>−{fmt(cart.bonusWriteoff)} грн</span>
          </div>
        )}
        <div className="pos-cart__totals-row pos-cart__totals-total">
          <span>До сплати:</span>
          <span>{fmt(cart.summary.total)} грн</span>
        </div>
        {willAccrue > 0 && (
          <div className="pos-cart__totals-row" style={{ color: 'var(--success)' }}>
            <span>+ Нарахується бонусів:</span>
            <span>{fmt(willAccrue)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
